/**
 * useOverwritePrompt
 *
 * Shared per-file "this file already exists" confirmation flow for the chatbox
 * (agent.vue) and the db file manager (dbfile.vue). When an upload hits an
 * existing file at the same path, the caller awaits promptOverwrite(filename),
 * which surfaces a NON-DISMISSIBLE modal (no backdrop/X/cancel — the user must
 * pick) offering three outcomes:
 *
 *   - 'skip'      — leave the existing file untouched; no upload, no (re)index.
 *   - 'reindex'   — keep the existing file, just (re)index it; no re-upload.
 *   - 'overwrite' — replace the file with the new upload.
 *
 * "Apply to all remaining" makes the chosen outcome sticky for the rest of the
 * current upload batch (call resetOverwriteBatch() when a new batch starts).
 *
 * Prompts are serialized so concurrent upload workers (dbfile.vue runs several)
 * never race on the single modal — they queue and resolve one at a time, and a
 * queued prompt resolves instantly once an earlier "apply to all" was chosen.
 *
 * The state is keyed and MODULE-LEVEL, not per component instance. An upload
 * outlives the page that started it (the queue keeps draining across route
 * changes), so a component-local prompt meant a file that hit "already exists"
 * after the user navigated away awaited a modal on a destroyed component: the
 * question was never rendered, the promise never resolved, and that upload was
 * suspended for good — holding its worker, and with it the rest of the batch.
 * Keyed state re-attaches the pending question to the page when it comes back.
 *
 * The key names the surface AND the project ("db:<serviceId>",
 * "chat:<serviceId>"): two projects uploading at once must not share one modal
 * or one "apply to all" choice, and neither must the chat and the files page.
 */
import { reactive } from "vue";

export type OverwriteChoice = "reindex" | "overwrite" | "skip";

export type OverwritePrompt = ReturnType<typeof createOverwritePrompt>;

const prompts = new Map<string, OverwritePrompt>();

export function useOverwritePrompt(key: string) {
    let existing = prompts.get(key);
    if (!existing) {
        existing = createOverwritePrompt();
        prompts.set(key, existing);
    }
    return existing;
}

function createOverwritePrompt() {
    const overwriteModal = reactive({
        open: false,
        filename: "",
        applyToAll: false,
    });

    let resolver: ((c: OverwriteChoice) => void) | null = null;
    let stickyChoice: OverwriteChoice | null = null;
    // Serializes prompts so two files never fight over the modal at once.
    let chain: Promise<unknown> = Promise.resolve();

    // Call at the start of each upload batch so "apply to all" doesn't leak
    // across separate uploads.
    function resetOverwriteBatch() {
        stickyChoice = null;
        overwriteModal.applyToAll = false;
    }

    function promptOverwrite(filename: string): Promise<OverwriteChoice> {
        if (stickyChoice) return Promise.resolve(stickyChoice);
        const run = chain.then(() => {
            // A prior file in the queue may have chosen "apply to all" while
            // this one waited — honor it without showing the modal again.
            if (stickyChoice) return stickyChoice;
            overwriteModal.filename = filename;
            overwriteModal.applyToAll = false;
            overwriteModal.open = true;
            return new Promise<OverwriteChoice>((resolve) => {
                resolver = resolve;
            });
        });
        // Keep the chain alive even if a prompt rejects (it shouldn't).
        chain = run.catch(() => undefined);
        return run;
    }

    // Wired to the modal's two buttons.
    function chooseOverwrite(choice: OverwriteChoice) {
        if (overwriteModal.applyToAll) stickyChoice = choice;
        overwriteModal.open = false;
        const r = resolver;
        resolver = null;
        r?.(choice);
    }

    return { overwriteModal, resetOverwriteBatch, promptOverwrite, chooseOverwrite };
}
